import _ from "lodash";
import React, { useState } from "react";

// DS
import { Button, IconButton, Modal, Tag } from "@toddle-edu/ds-web";
import { InformationOutlined, TickCircleFilled } from "@toddle-edu/ds-icons";

// Components
import CriteriaRatingV2 from "UIComponents/CriteriaRatingV2";
import I18nHOC from "UIComponents/I18nHOC";
import AIJustification from "UIComponents/AIJustification";

// External libs.
import { connect } from "react-redux";
import classNames from "classnames";
import { compose } from "lib/apolloClient";

// Modules
import {
  createDPRubricRatings,
  deleteDPRubricRatings,
} from "AssessmentEvaluation/modules/AssessmentEvaluationModule";

// Constants and Services
import { GENERIC_TAGS_INFO } from "modules/Services";

// Utils
import {
  getRatedScoreMemoized,
  getTotalScoreMemoized,
} from "AppComponents/CriteriaRubricAssessment/Utils";
import { getDPCriteriaRatedScore } from "ClassRoom/modules/utils";
import { getStyleStrippedText } from "Utils";

// Scss
import classes from "./DPAssessmentRubric.scss";
import SkeletonLoader from "UIComponents/SkeletonLoader";

const styles = {
  containerStyle: {
    width: "100%",
    overflowY: "auto",
    marginBottom: "20px",
  },
  modalBody: {
    paddingTop: 0,
  },
  containerStyleV2: {
    width: "100%",
    overflowY: "auto",
    marginBottom: "12px",
  },
};

const iconBtnProp = { type: "plain" };

const DPAssessmentRubric = ({
  assessmentTool,
  parentId,
  studentId,
  isEvaluationEnable,
  isEvaluated,
  parentType,
  userType,
  createDPRubricRatings,
  t,
  deleteDPRubricRatings,
  ratedCriteria,
  criteria: criterion,
  assessmentHeading,
  ibPYPElementRatings,
  tags = [],
  assessmentType,
  showLabel = false,
  academicTermId,
  sourceFrom,
  showUpdatedDesign,
  isAiEvaluationEnabled,
  onAcceptAIEvaluation,
  customSelectedRatingElement,
  isGeneratingAIResponse,
}) => {
  const [descriptionModalData, setDescriptionModalData] = useState({});

  const mode = isEvaluationEnable ? "edit" : "view";

  const isStaff = userType === "staff";

  const onSelectRubricAssessmentCriteriaRating = ({
    mark,
    isSelected = false,
    id,
  }) => {
    if (mode === "view" || !isStaff) return;

    const isAISelectedCriteria = _.find(ibPYPElementRatings, {
      ibPYPElementId: id,
      creatorType: "AI",
      isDeleted: false,
    });

    const isDeleteAiRatings = isAiEvaluationEnabled && isAISelectedCriteria;

    const params = {
      parentId,
      parentType,
      ibPYPElementId: id,
      studentId,
      academicTermId,
      ibPYPElementType: "DP_CRITERIA",
    };

    if (!isSelected || isAISelectedCriteria) {
      if (isDeleteAiRatings) {
        onAcceptAIEvaluation({
          ibPypElementId: id,
          academicTermId,
          ibPYPElementType: "DP_CRITERIA",
          value: _.toString(mark),
        });
      }
      createDPRubricRatings({
        ...params,
        isDeleteAiRatings,
        isPublished: isEvaluated,
        value: _.toString(mark),
        source: sourceFrom,
      });
    } else {
      deleteDPRubricRatings(params);
    }
  };

  const handleInfoClick = descriptors => {
    setDescriptionModalData(descriptors);
  };

  const getIsSelected = ({ achievementLevelLabel, id }) => {
    const [min, max] = _.split(achievementLevelLabel, "-") || [];

    const selectedValue = _.find(
      ratedCriteria,
      item => id === _.get(item, "ibPYPElementId", "")
    )?.value;

    if (_.isEmpty(max)) {
      return _.isEqual(min, selectedValue);
    } else {
      return _.inRange(selectedValue, min, +max + 1);
    }
  };

  const getTotalScore = ({ children = [] }) => {
    const totalScore = getTotalScoreMemoized({
      criterionIds: children,
      criteria: criterion,
    });

    const { value: totalRatedScore, showDash } = getRatedScoreMemoized({
      criterionIds: children,
      ratedCriteria,
    });

    return showDash ? "--" : `${totalRatedScore}/${totalScore}`;
  };

  const { totalRatedScore, totalScore, showDash } = getDPCriteriaRatedScore({
    ratedCriteria,
    assessmentTool,
    ibPYPElementRatings,
  });

  const scoreLabel = `${showDash ? "-" : totalRatedScore}/${totalScore}`;

  const label = _.get(assessmentTool, "label", "");

  const hasNullParents = _.every(criterion, item => _.isNil(item?.parent));

  const templateTitle = classNames("text-label-2", classes.templateTitle);
  const subTextClass = classNames("text-body-3", classes.subText);
  const assessmentTypeClass = classNames("text-body-3", classes.assessmentType);

  const descriptorLabelClasses = classNames(
    "text-label-3",
    classes.modalDescriptorLabel
  );

  const templateWithChildrenLabelClass = classNames(
    "text-label-2",
    classes.templateWithChildrenLabel
  );

  const teacherEvaluationScoreClasses = classNames({
    [classes.teacherEvaluationScore]: !showUpdatedDesign,
    ["text-body-l"]: showUpdatedDesign,
  });

  return (
    <div className={classes.container}>
      {showLabel ? (
        <div className={templateTitle}>
          {label}
          {!_.isEmpty(tags)
            ? _.map(tags, tag => {
                return (
                  <Tag
                    key={tag?.label}
                    size={"xx-small"}
                    color={_.get(
                      GENERIC_TAGS_INFO,
                      `${tag?.label}.color`,
                      "teal"
                    )}
                  >
                    {tag?.label}
                  </Tag>
                );
              })
            : null}
        </div>
      ) : null}

      {assessmentType && !isAiEvaluationEnabled ? (
        <div className={assessmentTypeClass}>{assessmentType}</div>
      ) : null}

      <div className={classes.tagContainer}>
        <Tag
          size={showUpdatedDesign ? "large" : "small"}
          dsVersion={showUpdatedDesign ? "2.0" : "1.0"}
        >
          <div className={teacherEvaluationScoreClasses}>
            {t("common:score_with_label_and_score", {
              label: t("common:total"),
              score: scoreLabel,
            })}
          </div>
        </Tag>
      </div>

      <div className={subTextClass}>{t("common:level_descriptors_info")}</div>

      <div className={classes.innerContainer}>
        {_.map(criterion, criteria => {
          const { id, label, children = [], parent, highestMark } =
            criteria || {};

          const aiIBPYPElement = _.find(ibPYPElementRatings, {
            ibPYPElementId: criteria.id,
            creatorType: "AI",
          });

          const justificationMessage = _.get(
            aiIBPYPElement,
            "evaluationJustification.message"
          );

          const isAISelectedCriteria = _.find(ibPYPElementRatings, {
            ibPYPElementId: criteria.id,
            creatorType: "AI",
            isDeleted: false,
          });

          const maxRating = _.get(criteria, "highestMark", 0);
          const aiSelectedCriteria = _.get(aiIBPYPElement, "value", "");

          const aiEvaluationLabel =
            aiSelectedCriteria && `${aiSelectedCriteria}/${maxRating}`;

          const criteriaLabelClasses = classNames({
            ["text-label-3"]: !showUpdatedDesign && !isAiEvaluationEnabled,
            [classes.criteriaLabel]: true,
            [classes.mediumFont]:
              _.some(criteria, item => !_.isEmpty(item)) &&
              !isAiEvaluationEnabled,
            [classes.singleLevelLabel]: !hasNullParents && _.isNil(parent),
            ["text-label"]: showUpdatedDesign || isAiEvaluationEnabled,
            [classes.criteriaLabelV2]: showUpdatedDesign,
          });

          const criteriaRatingContainerStyle = showUpdatedDesign
            ? styles.containerStyleV2
            : styles.containerStyle;

          return (
            <React.Fragment key={id}>
              {_.isEmpty(children) ? (
                <div className={classes.criteriaContainer}>
                  <div className={criteriaLabelClasses}>
                    {label || assessmentHeading}

                    <IconButton
                      icon={<InformationOutlined />}
                      size={showUpdatedDesign ? "small" : "x-small"}
                      variant={showUpdatedDesign ? "neutral" : "plain"}
                      onClick={() => handleInfoClick(criteria)}
                      dsVersion={showUpdatedDesign ? "2.0" : "1.0"}
                      {...(showUpdatedDesign ? iconBtnProp : {})}
                    />
                  </div>
                  {isGeneratingAIResponse ? (
                    <div className={classes.skeletonContainer}>
                      {_.times(highestMark, index => {
                        return (
                          <SkeletonLoader key={index} width={44} height={32} />
                        );
                      })}
                    </div>
                  ) : (
                    <CriteriaRatingV2
                      highestGrade={_.get(criteria, "highestGrade")}
                      highestMark={_.get(criteria, "highestMark")}
                      isEditable={isEvaluationEnable}
                      onSelectMark={onSelectRubricAssessmentCriteriaRating}
                      id={id}
                      type={_.get(criteria, "type", "")}
                      ratedCriteria={ratedCriteria}
                      containerStyle={criteriaRatingContainerStyle}
                      customRatingElementClass={
                        classes.customRatingElementClass
                      }
                      customSelectedRatingElement={
                        isAISelectedCriteria
                          ? classes.customAISelectedRatingElement
                          : customSelectedRatingElement
                      }
                    />
                  )}
                  {isStaff &&
                  isAiEvaluationEnabled &&
                  !_.isEmpty(justificationMessage) ? (
                    <div className={classes.justificationContainer}>
                      <AIJustification
                        justifications={[justificationMessage]}
                        aiEvaluationLabel={aiEvaluationLabel}
                        isAIGeneratingResponse={isGeneratingAIResponse}
                        isAIResponseAccepted={aiIBPYPElement?.isDeleted}
                      />
                    </div>
                  ) : null}
                </div>
              ) : (
                <>
                  <div className={templateWithChildrenLabelClass}>{label}</div>
                  <div className={classes.tagContainer}>
                    <Tag size="small">
                      <div className={classes.childTeacherEvaluationScore}>
                        {t("common:score_with_label_and_score", {
                          label: t("common:total"),
                          score: getTotalScore({ children }),
                        })}
                      </div>
                    </Tag>
                  </div>
                </>
              )}
            </React.Fragment>
          );
        })}
        <Modal
          hasOverlay={true}
          isOpen={!_.isEmpty(descriptionModalData)}
          onClose={() => handleInfoClick({})}
          shouldCloseOnEsc={true}
          width="568px"
          height="592px"
        >
          <Modal.Header>
            <div className="heading-4">{label}</div>
          </Modal.Header>
          <Modal.Body isScrollable={true} style={styles.modalBody}>
            <div
              className={classes.descriptionModal}
              dangerouslySetInnerHTML={{
                __html: getStyleStrippedText({
                  text: _.get(descriptionModalData, "subText", ""),
                  type: "SANITIZE_DANGER_HTML_PARAGRAPH",
                }),
              }}
            ></div>
            <div className={descriptorLabelClasses}>
              {t("common:descriptor_label")}
            </div>
            {_.map(
              _.get(descriptionModalData, "objectiveRubricDescriptors", []),
              descriptor => {
                const id = _.get(descriptionModalData, "id", "");
                const achievementLevelLabel = _.get(
                  descriptor,
                  "achievementLevel.label",
                  ""
                );
                const descriptorLabel = _.get(descriptor, "label", "");

                const isSelected = getIsSelected({
                  achievementLevelLabel,
                  id,
                });

                const levelLabelClasses = classNames({
                  [classes.levelsContainer]: true,
                  [classes.levelNumber]: true,
                  [classes.levelSelected]: isSelected,
                });

                const descriptorLabelClasses = classNames({
                  [classes.levelsContainer]: true,
                  [classes.levelSelected]: isSelected,
                });

                return (
                  <div
                    key={descriptor.id}
                    className={classes.descriptionContainer}
                  >
                    <div className={levelLabelClasses}>
                      {achievementLevelLabel}
                    </div>
                    <div className={descriptorLabelClasses}>
                      <span
                        className={classes.descriptorLabel}
                        dangerouslySetInnerHTML={{
                          __html: getStyleStrippedText({
                            text: descriptorLabel,
                            type: "SANITIZE_DANGER_HTML_PARAGRAPH",
                          }),
                        }}
                      ></span>
                      {isSelected ? (
                        <TickCircleFilled
                          variant="link"
                          size="xxx-small"
                          className={classes.tickIcon}
                        />
                      ) : null}
                    </div>
                  </div>
                );
              }
            )}
          </Modal.Body>
          <Modal.Footer>
            <div className={classes.modalFooter}>
              <Button
                variant="outlined-subtle"
                className={classes.closeButton}
                onClick={() => handleInfoClick([])}
              >
                {t("common:close")}
              </Button>
            </div>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
};

const mapStateToProps = state => {
  return {
    userType: _.get(state, "login.userInfo.user_type", ""),
  };
};

const mapActionCreators = {
  createDPRubricRatings,
  deleteDPRubricRatings,
};

export default compose(
  I18nHOC({ resource: ["common"] }),
  connect(mapStateToProps, mapActionCreators)
)(DPAssessmentRubric);
